import { FormikContext } from 'formik';
import { ReactElement } from 'react';
import { Overwrite } from 'utility-types';
import { IDataGridProps } from '../Grid/DataGrid';
import { IBaseInputProps } from '../Input';

export enum SelectorVerbosity {
  /**
   * Always shows x item(s)
   */
  SelectionCountOnly = 0,
  /**
   * Shows name of item if selected
   */
  SingleNameMultiCount,
  /**
   * Shows list of all item names
   */
  ItemNamesOnly
}

export interface ISelectorProps {
  /**
   * Children should be datagrids (or remote grids)
   */
  children: ReactElement<IDataGridProps<any>>;
  /**
   * Assets that are currently selected
   */
  value: ISelectorAsset[];
  /**
   * How to format the asset names for display when single
   * and multiple items are selected
   */
  assetFormat: IAssetFormat;
  /**
   * Name to use for forms
   * Also gets set as the id for the anchor tag
   */
  name: string;
  /**
   * Whether the selector handles multiple values
   * Passed to datagrid, needed primarily for validation
   * @default false
   */
  multiSelect?: boolean;
  /**
   * Text to show when nothing is selected
   * @default 'Any Asset'
   */
  placeholder?: string;
  /**
   * Sets the title attribute for the selector a tag
   */
  title?: string;
  /**
   * Sets the title for the selector modal
   * @default props.placeholder
   */
  modalTitle?: string;
  /**
   * Verbosity of the selector
   * None - Always shows x item(s)
   * Partial - Shows name of item if selected, but x items if multiple selected
   * Full - Shows list of all item names
   * @default None
   */
  verbosity?: SelectorVerbosity;
  required?: IBaseInputProps['required'];
  /**
   * Whether asset is valid (used if required is true)
   * @default true
   */
  valid?: boolean;
  /**
   * Whether asset is touched (used in validation)
   * @default false
   */
  touched?: boolean;
  /**
   * The property selector will treat as 'name' for its display
   * If a grid has a different column than name (e.g., message_name)
   * it can be configured here
   * @default 'name'
   */
  nameProperty?: string;
  /**
   * ID for QA purposes
   */
  'qa-id'?: string;
  /**
   * You can run independent field-level validations by passing a function to the validate prop.
   * The function will respect the validateOnBlur and validateOnChange props specified in the parent(Form).
   * This function can be either be synchronous or asynchronous
   */
  validate?: (value: any) => string | Promise<void> | undefined;
  /**
   * Selection handler for when modal closes, gets passed newly selected assets
   * Works automatically with Formik
   */
  onChange(selected: ISelectorAsset[]): void;
  /**
   * Handle blur for validation
   */
  onBlur?(e: any): void;
}

export interface ISelectorState {
  modalOpen: boolean;
  selected: ISelectorAsset[];
  selectedMap: { [key: string]: ISelectorAsset };
}

export interface IAssetFormat {
  single: string;
  multiple: string;
}

export interface ISelectorAsset {
  id: string | number;
  name: string;
}

export interface IBaseSelectorProps extends ISelectorProps {
  formik: FormikContext<any>;
}

export type InnerSelectorProps = Overwrite<
  ISelectorProps,
  ISelectorWithContextProps
> & { formik: FormikContext<any> };

/**
 * This wrapper is here because selector needs selected etc as props to set as
 * state. Injects formik stuff as props
 */

export interface ISelectorWithContextProps {
  /**
   * Assets that are currently selected
   */
  value?: ISelectorAsset[];
  /**
   * Selection handler for when modal closes, gets passed newly selected assets
   * Works automatically with Formik
   */
  onChange?(selected: ISelectorAsset[]): void;
}
